import { Spinner } from "@heroui/react";
import {
  ArrowLeft,
  ArrowRight,
  Close,
  Photos,
} from "assets/icons";
import  { useCallback, useEffect, useState } from "react";
import { FileLabel, FileLog, SecondaryLabel } from "shared/types/file";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { PhotoCard } from "./photoCard";

export interface MultiSelectPhotosProps {
  incomingBuildingId: string;
  incomingAcquisitionId?: string;
  incomingFileLabel?: FileLabel;
  incomingSecondaryLabel: SecondaryLabel;
  // Photos whose (upload) label matches one of these are always included by the
  // backend document generator, regardless of secondary tags. They are shown as
  // selected and cannot be deselected here.
  incomingAlwaysIncludedFileLabels?: string[];
  handlePictureSelect?: (selectedPicture: number) => void;
  incomingUnitId: string;
  page?: number;
  pageSize?: number;
}

const MultiSelectPhotos = ({ incomingBuildingId, incomingUnitId, incomingFileLabel, incomingAcquisitionId, incomingSecondaryLabel, incomingAlwaysIncludedFileLabels, pageSize = 10, handlePictureSelect }: MultiSelectPhotosProps) => {
  const [pictures, setPictures] = useState<FileLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [buildingId, setBuildingId] = useState<string | undefined>(incomingBuildingId);
  const [acquisitionId, setAcquisitionId] = useState<string | undefined>(incomingAcquisitionId);
  const [unitId, setUnitId] = useState<string | undefined>(incomingUnitId);
  const [fileLabel, setFileLabel] = useState<string | undefined>(incomingFileLabel);
  const [secondaryLabel, setSecondaryLabel] = useState<SecondaryLabel | undefined>(incomingSecondaryLabel);
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isImagePickerVisible, setIsImagePickerVisible] = useState(false);
  const { session } = useSession();

  useEffect(() => {
    console.log("incomingBuildingId", incomingBuildingId);
    setBuildingId(incomingBuildingId);
  }, [incomingBuildingId]);

  useEffect(() => {
    console.log("incomingAcquisitionId", incomingAcquisitionId);
    setAcquisitionId(incomingAcquisitionId);
  }, [incomingAcquisitionId]);

  useEffect(() => {
    console.log("incomingUnitId", incomingUnitId);
    setUnitId(incomingUnitId);
  }, [incomingUnitId]);

  useEffect(() => {
    console.log("incomingFileLabel", incomingFileLabel);
    setFileLabel(incomingFileLabel);
  }, [incomingFileLabel]);

  useEffect(() => {
    console.log("incomingSecondaryLabel", incomingSecondaryLabel);
    setSecondaryLabel(incomingSecondaryLabel);
  }, [incomingSecondaryLabel]);

  const fetchImagesWithLabel = useCallback(async () => {
    let isMounted = true;
    const controller = new AbortController();

    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        buildingId: buildingId || "",
        unitId: unitId || "",
        acquisitionId: acquisitionId || "",
        fileLabel: "",
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
      }).toString();
      let response = await fetchWithAuth(
        `${API_URL}/file/get-images?${queryParams}`,
        { method: "GET" },
        session,
      );

      const result = await (response as any).json();
      const images = result.data || result;

      if (isMounted && images && images.length > 0) {
        setPictures(images);
        setSelectedPhotos(
          images
            .filter((photo: FileLog) =>
              (secondaryLabel && photo.secondaryLabels?.includes(secondaryLabel)) ||
              isAlwaysIncluded(photo),
            )
            .map((photo: FileLog) => photo.id as number),
        );
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      if (isMounted) setIsLoading(false);
    }
    return () => {
      isMounted = false;
      controller.abort();
      console.log("Fetch aborted by cleanup");
    };
  }, [
    buildingId,
    unitId,
    fileLabel,
    acquisitionId,
    currentPage,
    pageSize,
    session,
  ]);

  useEffect(() => {
    if (isImagePickerVisible) {
      fetchImagesWithLabel();
    }
  }, [incomingBuildingId, incomingUnitId, incomingAcquisitionId, fileLabel, secondaryLabel, currentPage, isImagePickerVisible]);


  // Photos in an always-included folder (e.g. "Fotos ingaande plaatsbeschrijving")
  // are picked up by the document generator regardless of secondary tags.
  const isAlwaysIncluded = (photo: FileLog) => {
    if (!incomingAlwaysIncludedFileLabels?.length) return false;
    const photoLabels = Array.isArray(photo.label) ? photo.label : [photo.label];
    return photoLabels.some((label) =>
      incomingAlwaysIncludedFileLabels.includes(String(label)),
    );
  };

  const updateSecondaryTag = async (id: number, action: "add-secondary-tag" | "remove-secondary-tag") => {
    const response = await fetchWithAuth(
      `${API_URL}/file/${action}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileLogId: id,
          tag: secondaryLabel,
        }),
      },
      session,
    );

    if (!(response as Response).ok) {
      throw new Error(
        `Failed to ${action} "${secondaryLabel}" on fileLog ${id}: ${(response as Response).status}`,
      );
    }

    // Keep local state in sync so the selected ring stays truthful after toggling
    setPictures((prev) =>
      prev.map((photo) => {
        if (photo.id !== id) return photo;
        const currentTags = photo.secondaryLabels || [];
        return {
          ...photo,
          secondaryLabels:
            action === "add-secondary-tag"
              ? [...currentTags.filter((tag) => tag !== secondaryLabel), secondaryLabel as SecondaryLabel]
              : currentTags.filter((tag) => tag !== secondaryLabel),
        };
      }),
    );
  };

  const togglePhotoSelection = async (id: number) => {
    const photo = pictures.find((picture) => picture.id === id);
    if (photo && isAlwaysIncluded(photo)) {
      toast.info(
        "Deze foto zit in een map die altijd wordt opgenomen in het document en kan hier niet worden gedeselecteerd.",
      );
      return;
    }

    const isCurrentlySelected = selectedPhotos.includes(id);

    // Adding tags in the backend is what drives the document generator logic
    try {
      await updateSecondaryTag(id, isCurrentlySelected ? "remove-secondary-tag" : "add-secondary-tag");
    } catch (error) {
      console.error("Error updating secondary tag:", error);
      Sentry.captureException(error, {
        tags: { component: "MultiSelectPhotos" },
        extra: { fileLogId: id, secondaryLabel, isCurrentlySelected },
      });
      toast.error("Fout bij het selecteren van de foto, probeer opnieuw.");
      return;
    }

    setSelectedPhotos(prev =>
      prev.includes(id)
        ? prev.filter((photoId) => photoId !== id)
        : [...prev, id],
    );

    // This is a callback to the parent component to handle the selected photo
    // Currently has no use, just for potential flexibility
    if (handlePictureSelect) handlePictureSelect(id);
  };

  const clearSelection = () => {
    setSelectedPhotos([]);
  };

  const handleNextPage = () => setCurrentPage(currentPage + 1);
  const handlePreviousPage = () =>
    setCurrentPage(currentPage - 1 > 0 ? currentPage - 1 : 1);

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="flex items-center gap-1 text-lg">
            <Photos className="h-[18px] w-[18px]" /> Selecteer foto's
          </p>
          <button
            onClick={() => setIsImagePickerVisible(!isImagePickerVisible)}
            className="flex items-center gap-2 rounded-md bg-moss-primary px-3 py-1.5 text-sm text-white hover:bg-moss-primary/90 transition-colors"
          >
            {isImagePickerVisible ? 'Verberg afbeeldingen' : 'Toon afbeeldingen'}
          </button>
        </div>
        <div className="flex items-center text-sm text-neutral-secondary-mid *:px-2">
          {/* <div className="flex justify-center gap-2.5">
            <SearchComponent
              label="Zoek gebouw of unit"
            // onSelect={(resource: any) =>
            //   handleSelectBuildingOrUnit(resource)
            // }
            // mode={SearchMode.SEARCH_AND_PRESENT}
            // editMode={true}
            // resourceFilter={[ResourceType.BUILDING, ResourceType.UNIT]}
            />
            <FileLabelSearchComponent
            // labels={Object.values(FileLabel)}
            // incomingSelectedLabel={fileLabel}
            // onSelect={(selectedLabel: any) => {
            //   setFileLabel(selectedLabel);
            //   setSelectedFileLabel(selectedLabel);
            // }}
            />
          </div> */}
          <p>
            Pagina {currentPage}
          </p>
          <p>
            {selectedPhotos.length} foto
            {selectedPhotos.length !== 1 ? "'s" : ""} geselecteerd
          </p>
          {selectedPhotos.length > 0 && (
            <button
              onClick={clearSelection}
              className="rounded-full bg-status-bad p-1.5 text-white"
            >
              <Close className="h-[18px] w-[18px]" />
            </button>
          )}

          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`rounded-full p-1.5 ${currentPage === 0
                  ? "bg-moss-primary/50"
                  : "bg-moss-primary"
                } text-white`}
            >
              <ArrowLeft className="h-[18px] w-[18px]" />
            </button>
            <button
              onClick={handleNextPage}
              className="rounded-full bg-moss-primary p-1.5 text-white"
            >
              <ArrowRight className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </header>
      {isImagePickerVisible && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner
                label="Gegevens aan het ophalen"
                color="success"
                size="md"
              />
            </div>
          ) : (pictures.map((photo: FileLog) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              incomingIsSelected={isAlwaysIncluded(photo) || selectedPhotos.includes(photo.id as number)}
              onClick={() => togglePhotoSelection(photo.id as number)}
            />
          )))}
        </div>
      )}
      {!isImagePickerVisible && selectedPhotos.length > 0 && (
        <div className="text-sm text-neutral-secondary-mid px-2">
          {selectedPhotos.length} foto{selectedPhotos.length !== 1 ? "'s" : ""} geselecteerd
        </div>
      )}
    </div>
  );
};

export default MultiSelectPhotos;
